import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';

// Breakpoint below which the sidebar auto-collapses to icon-only so prayer
// cards still get enough horizontal room to render at a comfortable size.
// Tuned around a 3-column card grid: full sidebar (240px) + 3 * ~300px card
// + gutters ≈ 1200px, so anything below ~1200px is uncomfortable.
const AUTO_COLLAPSE_MAX_WIDTH = 1199;
const MOBILE_MAX_WIDTH = 767;

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;

const isNarrowViewport = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia(`(max-width: ${AUTO_COLLAPSE_MAX_WIDTH}px)`).matches;

const Layout = ({ defaultMini = false }) => {
  const location = useLocation();
  const isWatch = location.pathname === '/watch';

  // Track whether the viewport is currently too narrow to comfortably show
  // both the expanded sidebar and the prayer grid. Recomputed on resize.
  const [narrow, setNarrow] = useState(() => isNarrowViewport());

  // Track the user's explicit preference so we can restore the expanded
  // sidebar when they widen the window back up. Initial value follows the
  // existing behaviour: expanded on wide desktop, collapsed everywhere else.
  const [userWantsOpen, setUserWantsOpen] = useState(() =>
    !defaultMini && !isWatch && !isNarrowViewport()
  );

  // Watch the auto-collapse breakpoint and update `narrow`. Using
  // matchMedia keeps this cheap and native — no throttled resize loops.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${AUTO_COLLAPSE_MAX_WIDTH}px)`);
    const onChange = (e) => setNarrow(e.matches);
    // Safari <14 uses `addListener` — support both.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Reset sidebar state when navigating. On mobile devices we auto-collapse
  // after every navigation so tapping a sidebar link reveals the destination
  // page instead of leaving the drawer open on top of it. Wider desktops
  // remember the user's preference.
  useEffect(() => {
    if (isWatch) {
      setUserWantsOpen(false);
      return;
    }
    if (isMobileViewport()) {
      setUserWantsOpen(false);
    } else if (!isNarrowViewport()) {
      // Only auto-open on comfortably wide desktops. Narrow desktops keep
      // whatever the user set explicitly (usually the default: collapsed).
      setUserWantsOpen(true);
    }
  }, [location.pathname, isWatch]);

  // Effective sidebar state: user's preference AND-ed with the width gate.
  // On narrow viewports we always force the icon-only mini rail.
  const sidebarOpen = userWantsOpen && !narrow;

  const handleMenuToggle = () => {
    // On narrow viewports the header menu button becomes a temporary
    // "expand while viewing" toggle. On wide desktops it flips the pref.
    setUserWantsOpen((o) => !o);
    if (narrow) setNarrow(false); // let the user override for this session
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f0f0f] text-zinc-900 dark:text-white">
      <Header onMenuToggle={handleMenuToggle} />
      <div className="flex">
        {sidebarOpen ? (
          <Sidebar open={true} mini={false} />
        ) : (
          <Sidebar open={false} mini={true} />
        )}
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
